import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ChevronLeft, RefreshCw, CheckCircle, XCircle, 
  Clock, Send, BarChart3, Mail, Users, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-gray-100 text-gray-800',
  sent: 'bg-green-100 text-green-800',
  delivered: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
  bounced: 'bg-orange-100 text-orange-800'
};

const statusIcons = {
  pending: Clock,
  sent: Send,
  delivered: CheckCircle,
  failed: XCircle,
  bounced: AlertCircle
};

const EmailLogs = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const token = localStorage.getItem('admin_token');

  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [campaignId, token, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [campaignRes, statsRes, sendsRes] = await Promise.all([
        fetch(`${API_URL}/api/email/campaigns/${campaignId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/email/campaigns/${campaignId}/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/email/campaigns/${campaignId}/sends`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (!campaignRes.ok) throw new Error('Campaign not found');
      
      const campaignData = await campaignRes.json();
      const statsData = await statsRes.json();
      const sendsData = await sendsRes.json();
      
      setCampaign(campaignData);
      setStats(statsData);
      setSends(sendsData);
    } catch (error) {
      toast.error('Failed to load campaign logs');
      navigate('/admin/email');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const filteredSends = filter === 'all' 
    ? sends 
    : sends.filter(s => s.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#00457f] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#00457f] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/email')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Campaign Logs
              </h1>
              <p className="text-white/70 text-sm">{campaign?.name}</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            className="border-white/30 text-white hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_recipients}</p>
                    <p className="text-gray-500 text-sm">Total Recipients</p>
                  </div>
                  <Users className="w-10 h-10 text-gray-300" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-green-600">{stats.sent_count}</p>
                    <p className="text-gray-500 text-sm">Sent</p>
                  </div>
                  <Send className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-blue-600">{stats.delivered_count}</p>
                    <p className="text-gray-500 text-sm">Delivered</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-red-600">{stats.failed_count}</p>
                    <p className="text-gray-500 text-sm">Failed</p>
                  </div>
                  <XCircle className="w-10 h-10 text-red-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Campaign Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Campaign Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Status</p>
                <p className="font-medium capitalize">{campaign?.status}</p>
              </div>
              <div>
                <p className="text-gray-500">Subject</p>
                <p className="font-medium truncate">{campaign?.subject}</p>
              </div>
              <div>
                <p className="text-gray-500">Created</p>
                <p className="font-medium">{formatDate(campaign?.created_at)}</p>
              </div>
              <div>
                <p className="text-gray-500">Sent At</p>
                <p className="font-medium">{formatDate(campaign?.sent_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Send Logs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Send Logs ({sends.length})</CardTitle>
              <div className="flex gap-2">
                {['all', 'sent', 'delivered', 'failed', 'pending'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                      filter === status
                        ? 'bg-[#00457f] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredSends.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No send logs found for this filter.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium">Recipient</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Sent At</th>
                      <th className="text-left p-3 font-medium">Message ID</th>
                      <th className="text-left p-3 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSends.map((send) => {
                      const StatusIcon = statusIcons[send.status] || AlertCircle;
                      return (
                        <tr key={send.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">{send.recipient_email}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${statusColors[send.status]}`}>
                              <StatusIcon className="w-3 h-3" />
                              {send.status}
                            </span>
                          </td>
                          <td className="p-3 text-gray-500">{formatDate(send.sent_at)}</td>
                          <td className="p-3 text-gray-500 font-mono text-xs truncate max-w-[200px]">
                            {send.sendgrid_message_id || '-'}
                          </td>
                          <td className="p-3 text-red-600 text-xs max-w-[200px] truncate">
                            {send.error_message || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EmailLogs;
